import { Box, Stack } from '@mui/material';

import { Button } from '@/components/form/baseInputs/Button';
import { FormAwareCheckbox } from '@/components/form/formAwareInputs/FormAwareCheckbox';
import { FormAwareTypeaheadSelectInput } from '@/components/form/formAwareInputs/FormAwareTypeaheadSelectInput';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { FormProvider, useForm } from '@/components/react-hook-form';
import { useReportError } from '@/hooks/useReportError';
import { downloadBlob } from '@/modules/files/utils/fileUtils';

import { getClientsOptions, useClientData } from './ClientImportExport.utils';
import {
  createDeidentifiedOcrArchive,
  getSafeOcrHouseholdName,
} from './deidentifiedOcrArchive.utils';
import { useExportDeidentifiedHouseholdOcrLazyQuery } from './graphql/ClientImportExport.generated';

interface DeidentifiedOcrExportForm {
  householdId: string;
  synthetic: boolean;
}

export function ClientDeidentifiedOcrExport() {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { loading: clientsLoading, clientsData } = useClientData();
  const [exportOcr] = useExportDeidentifiedHouseholdOcrLazyQuery({
    fetchPolicy: 'no-cache',
    errorPolicy: 'none',
  });
  const methods = useForm<DeidentifiedOcrExportForm>({
    defaultValues: { householdId: '', synthetic: true },
  });
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  async function onExport(values: DeidentifiedOcrExportForm) {
    try {
      const result = await exportOcr({
        variables: {
          householdId: values.householdId,
          options: { synthetic: values.synthetic },
        },
      });
      if (result.error || result.errors?.length) {
        throw (
          result.error ??
          new Error('The deidentified OCR query returned errors.')
        );
      }
      const household = result.data?.household;
      if (household?.__typename !== 'Household') {
        throw new Error(
          'The household was not found in the deidentified OCR response.'
        );
      }
      const archive = await createDeidentifiedOcrArchive(household);
      downloadBlob(
        archive,
        `${getSafeOcrHouseholdName(household.id)}_deidentified_ocr.tar.gz`
      );
    } catch (error) {
      showFeedback('Failed to export deidentified OCR artifacts.', {
        variant: 'error',
      });
      reportError(
        'Failed to export deidentified OCR artifacts',
        error instanceof Error ? error : new Error(String(error))
      );
    }
  }

  return (
    <FormProvider {...methods}>
      <Stack
        component="form"
        noValidate
        spacing={2}
        onSubmit={handleSubmit(onExport)}
      >
        <FormAwareTypeaheadSelectInput<DeidentifiedOcrExportForm>
          label="Household for deidentified OCR export"
          fieldName="householdId"
          required
          control={control}
          options={getClientsOptions(clientsData)}
          disabled={isSubmitting || clientsLoading}
        />
        <FormAwareCheckbox
          label="Use synthetic replacements"
          fieldName="synthetic"
          control={control}
          disabled={isSubmitting}
        />
        <Box>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            loading={isSubmitting}
            disabled={clientsLoading}
          >
            Download deidentified OCR (.tar.gz)
          </Button>
        </Box>
      </Stack>
    </FormProvider>
  );
}
